import { useEffect, useMemo } from 'react'

import { useSectionConfigProperty } from 'conversations/hooks/useSections'
import { getConversation, getTagsById } from 'conversations/modules/conversation/selectors'
import {
  getConversationChannelPrefix,
  getSocketSetupComplete,
} from 'conversations/modules/socket/selectors'
import { getActiveOrgUsers } from 'conversations/modules/user/selectors'
import { isCurrentConversationId } from 'conversations/utils/sectionUtils'
import { isDefined } from 'conversations/utils/typescriptHelpers'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import {
  receivedConversationSync,
  receiveMessageReadReceipt,
} from 'shared/store/actions/conversation'
import {
  receivedPingPongEndUserPresence,
  recievedUserFinishedTyping,
  recievedUserStartedTyping,
} from 'shared/store/actions/endUser'
import { receiveMessage } from 'shared/store/actions/message'
import Conversation from 'shared/typings/conversations/Conversation'
import { UserTypingMessage } from 'shared/typings/endUser/Presence'
import { MessageWithId } from 'shared/typings/messages/Message'
import { MessageRecipientStatus } from 'shared/typings/messages/MessageRecipientStatus'
import { Tag as ITag } from 'shared/typings/tags/Tag'
import { User } from 'shared/typings/user/User'

import { CLUSTER, getDriftWebSocket } from '@driftt/drift-ws'

import { RootAction, RootState } from 'typesafe-actions'

import { useConversationId } from './useConversationId'

interface ConversationSyncPayload {
  syncTime: number
}

export const useListenForTypingIndicators = (conversationId: number | undefined) => {
  const socketSetupComplete = useSelector<RootState, boolean>(getSocketSetupComplete)
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const channelPrefix = useSelector(getConversationChannelPrefix)
  const channelName = `${channelPrefix}-${conversationId}`

  useEffect(() => {
    if (socketSetupComplete && conversationId) {
      const ws = getDriftWebSocket()

      const typingStartSub = ws
        .on<UserTypingMessage>({
          cluster: 'chat',
          channelName,
          messageName: 'typing:started',
        })
        .subscribe((msg) => {
          dispatch(recievedUserStartedTyping(msg))
          if (msg.userType === 'END_USER') {
            dispatch(receivedPingPongEndUserPresence({ id: `${msg.userId}` }))
          }
        })

      const typingEndSub = ws
        .on<UserTypingMessage>({
          cluster: 'chat',
          channelName,
          messageName: 'typing:finished',
        })
        .subscribe((msg) => {
          dispatch(recievedUserFinishedTyping(msg))
          if (msg.userType === 'END_USER') {
            dispatch(receivedPingPongEndUserPresence({ id: `${msg.userId}` }))
          }
        })

      return () => {
        typingStartSub.unsubscribe()
        typingEndSub.unsubscribe()
      }
    }
  }, [socketSetupComplete, dispatch, conversationId, channelName])
}

export const useListenForConversationSync = (conversationId: number | undefined) => {
  const socketSetupComplete = useSelector<RootState, boolean>(getSocketSetupComplete)
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const channelPrefix = useSelector(getConversationChannelPrefix)
  const channelName = `${channelPrefix}-${conversationId}`

  useEffect(() => {
    if (socketSetupComplete && conversationId) {
      const ws = getDriftWebSocket()
      const subscription = ws
        .on<ConversationSyncPayload>({
          cluster: CLUSTER.CHAT,
          channelName,
          messageName: 'conversation-synced',
        })
        .subscribe((conversationSyncPayload) =>
          dispatch(receivedConversationSync({ syncTime: conversationSyncPayload.syncTime }))
        )

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [conversationId, socketSetupComplete, dispatch, channelName])
}

export const useListenReadReceipts = (conversationId: number | undefined) => {
  const isSocketSetup = useSelector<RootState, boolean>(getSocketSetupComplete)
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const channelPrefix = useSelector(getConversationChannelPrefix)
  const channelName = `${channelPrefix}-${conversationId}`

  useEffect(() => {
    if (conversationId && isSocketSetup) {
      const ws = getDriftWebSocket()
      const subscription = ws
        .on<MessageRecipientStatus>({
          cluster: CLUSTER.CHAT,
          channelName,
          messageName: 'message-recipient-status',
        })
        .subscribe((receipt) => {
          // TODO: This isn't actually compatible with > MAX_SAFE_INT ids
          const stringifiedRecipientIdReadReceipt = {
            ...receipt,
            recipientId: `${receipt.recipientId}`,
          }

          dispatch(receiveMessageReadReceipt(stringifiedRecipientIdReadReceipt))
        })

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [conversationId, isSocketSetup, dispatch, channelName])
}

export const useListenForMessageNew = (
  conversationId: number | undefined,
  sectionProps?: SectionProps
) => {
  const isSocketSetup = useSelector<RootState, boolean>(getSocketSetupComplete)
  const dispatch = useDispatch<Dispatch<RootAction>>()
  const channelPrefix = useSelector(getConversationChannelPrefix)
  const channelName = `${channelPrefix}-${conversationId}`
  const sectionType = useSectionConfigProperty(sectionProps?.sectionID, 'type')
  const listenForNewMessages =
    sectionType === 'my-convos' && sectionProps?.selectedPill !== 'closed'
  const doSubscribe =
    conversationId && (isCurrentConversationId(conversationId) || listenForNewMessages)

  useEffect(() => {
    if (isSocketSetup && doSubscribe) {
      const ws = getDriftWebSocket()
      const subscription = ws
        .on<MessageWithId>({
          cluster: CLUSTER.CHAT,
          channelName,
          messageName: 'message-new',
        })
        .subscribe((message) => {
          dispatch(receiveMessage(message))
        })

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [doSubscribe, isSocketSetup, dispatch, channelName])
}
interface SectionProps {
  sectionID: string
  selectedPill: string
}
export const useConversation = (
  conversationId: number | undefined,
  sectionProps?: SectionProps
): Conversation | undefined => {
  useListenForConversationSync(conversationId)
  useListenReadReceipts(conversationId)
  useListenForMessageNew(conversationId, sectionProps)
  useListenForTypingIndicators(conversationId)

  return useSelector<RootState, Conversation | undefined>(getConversation(conversationId ?? -1))
}

export const useCurrentConversation: () => Conversation | undefined = () => {
  const conversationId = useConversationId()
  return useConversation(conversationId)
}

export const useConversationParticipants = (conversationId: number | undefined): User[] => {
  const conversation = useConversation(conversationId)
  const participantIds = conversation?.participantIds
  const activeOrgUsers = useSelector<RootState, User[]>(getActiveOrgUsers)
  return useMemo(() => {
    return activeOrgUsers.filter((user) => {
      return participantIds?.includes(user.id) ?? false
    })
  }, [activeOrgUsers, participantIds])
}

export const useConversationTags = (conversationId: number | undefined): ITag[] => {
  const conversation = useConversation(conversationId)
  const tagIds = conversation?.tags
  const tagsById = useSelector<RootState, Record<number, ITag | undefined>>(getTagsById)
  return useMemo(
    () => tagIds?.map((tagId) => tagsById[tagId]).filter(isDefined) ?? [],
    [tagIds, tagsById]
  )
}
